import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";
import type { FragmentRefs } from "relay-runtime";

import { theme } from "shared/lib/styles";

import { AdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import type { topicTopContentsTopic$key } from "scmp-app/queries/__generated__/topicTopContentsTopic.graphql";

import {
  AdsSlotContainer,
  StyledTopStoriesItem,
  TopContentsContainer,
  TopContentsLeftContainer,
  TopContentsRightContainer,
} from "./styles";

type Props = {
  currentTopicReference?: string;
  index: number;
  reference: topicTopContentsTopic$key;
  topContents: {
    content: {
      readonly " $fragmentSpreads": FragmentRefs<
        | "homeSecondaryContentItemContent"
        | "sectionTopLeadPrimaryContentItemContent"
        | "topStoriesItemContent"
      >;
    }[];
    type: string;
  };
};

export const TopicTopContents: FunctionComponent<Props> = ({
  currentTopicReference,
  index,
  reference,
  topContents,
}) => {
  const data = useFragment(
    graphql`
      fragment topicTopContentsTopic on Topic {
        entityId
        advertZone(version: 2)
      }
    `,
    reference,
  );

  if (!topContents.content) {
    return null;
  }
  const [first, second, ...rest] = topContents.content;
  const isShowAds = (index + 1) % 3 === 1; // Show ads in every first block

  const renderLastItemAd = () => {
    if (!isShowAds) return null;
    return (
      <AdsSlotContainer>
        <AdSlot
          adUnit={index === 0 ? "d_native2a" : "d_native2c"}
          breakpoint={theme.breakpoints.up("desktop")}
          customizedAdUnitTargeting={index === 0 ? "dnative-7a" : "dnative-7b"}
          sizes={[[1, 1], "fluid"]}
          targeting={{
            paid: TargetingPaidType.NotArticle,
            sctid: data?.entityId,
          }}
          zone={data?.advertZone ?? "default"}
        />
        <AdSlot
          adUnit={index === 0 ? "m_native2a" : "m_native2c"}
          breakpoint={theme.breakpoints.only("tablet")}
          customizedAdUnitTargeting={index === 0 ? "dnative-7a" : "dnative-7b"}
          sizes={[[1, 1], "fluid"]}
          targeting={{
            paid: TargetingPaidType.NotArticle,
            sctid: data?.entityId,
          }}
          zone={data?.advertZone ?? "default"}
        />
        <AdSlot
          adUnit={index === 0 ? "m_native2a" : "m_native2c"}
          breakpoint={theme.breakpoints.only("mobile")}
          customizedAdUnitTargeting={index === 0 ? "dnative-7a" : "dnative-7b"}
          sizes={[[1, 1], "fluid"]}
          targeting={{
            paid: TargetingPaidType.NotArticle,
            sctid: data?.entityId,
          }}
          zone={data?.advertZone ?? "default"}
        />
      </AdsSlotContainer>
    );
  };

  return (
    <TopContentsContainer>
      <TopContentsLeftContainer>
        {first && (
          <StyledTopStoriesItem
            currentTopicReference={currentTopicReference}
            reference={first}
            topicLinkVariant={{ type: "secondary" }}
            variant="primary"
            withCaption={false}
            // SCMPWEB-9814: carousel on the first 3 articles with images (the hero
            // leading article plus the primary item of the first two blocks)
            withCarousel={index <= 1}
            withSection={false}
            withTopic
          />
        )}
        {second && (
          <StyledTopStoriesItem
            currentTopicReference={currentTopicReference}
            reference={second}
            topicLinkVariant={{ type: "secondary" }}
            variant="secondary"
            withSection={false}
            withTopic
          />
        )}
      </TopContentsLeftContainer>
      <TopContentsRightContainer>
        {rest.map((item, itemIndex) => (
          <StyledTopStoriesItem
            currentTopicReference={currentTopicReference}
            isShowImage={!isShowAds && itemIndex === rest.length - 1}
            key={itemIndex}
            reference={item}
            topicLinkVariant={{ type: "secondary" }}
            variant="secondary"
            withSection={false}
            withTopic
          />
        ))}
        {renderLastItemAd()}
      </TopContentsRightContainer>
    </TopContentsContainer>
  );
};

TopicTopContents.displayName = "TopicTopContents";
